import React from 'react';
import { getCoreAPIUrl } from './urlHelper';
import { getSearchParameter } from './location-helper';
import { redirect, redirectToDecline } from './parent-helper';
import { PotentialAnswers } from '../components/inputs/Select';
import * as Sentry from '@sentry/react';
import { logger, loggerError } from './logger';
import { commonReducerActions, IAction } from '../store/CommonContext';
import * as icons from '@fortawesome/free-solid-svg-icons';

export interface IEligibilityApprove {}

enum EligibilityResult {
  NOT_ELIGIBLE = 'NOT_ELIGIBLE',
  DECLINED = 'DECLINED',
  ORDER_DECLINED = 'ORDER_DECLINED',
  ORDER_APPROVED = 'ORDER_APPROVED',
  CONDITIONAL_CONSUMER_INTERACTION = 'CONDITIONAL_CONSUMER_INTERACTION',
  PROCESSING = 'PROCESSING',
  WAITING_FOR_APPROVAL = 'WAITING_FOR_APPROVAL',
}

export interface IEligibilityApproveResponse {
  result: EligibilityResult;
  parameters: {
    Redirect?: 'Approve' | 'Decline';
    AutoRedirectUrl?: string;
  };
}

export const eligibilityApprove = (): Promise<IEligibilityApproveResponse> => {
  const orderSessionKey = getSearchParameter('orderSessionKey');
  const mid = getSearchParameter('mid');
  const senseKey = getSearchParameter('senseKey');

  const url = `${getCoreAPIUrl('eligibility')}/approve`;
  const body = {
    orderSessionKey,
    senseKey,
    mid,
  };
  const options = {
    method: 'POST',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  };

  const transaction = Sentry.startTransaction({ name: 'POST_approve' });
  const span = transaction.startChild({
    op: 'http',
    description: `POST ${url}`,
  });

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return fetch(url, options)
    .then(response => response.json())
    .then(response => {
      span.setData('GET challenge response', JSON.stringify(response));
      span.finish();
      transaction.finish();

      return response;
    })
    .catch(error => {
      span.setData('GET challenge response error', error.message);
      span.finish();
      transaction.finish();

      loggerError(error);
      redirectToDecline();
    });
};

export interface IChallengeItem {
  children: IChallengeItem[] | null;
  attributes: { [key: string]: string } | null;
  component: string;
  dataId: string | null;
  defaultValue: string | number | null;
  description: string | null;
  inputType: string | null;
  mask: string | null;
  placeholder: string | null;
  text: string | null;
  tooltip: string | null;
  order: number;
  potentialAnswers: PotentialAnswers[];
  validations: { [key: string]: string }[] | number;
  classes: string[];
  uniqueId: string;
  src: string | null;
  properties?: {
    imageUrl?: string;
    imageBase64?: string;
    imageName?: string;
    backgroundColor?: string;
    iconColor?: string;
    color?: string;
    fontAwesomeIcon?: keyof typeof icons;
    animationName?: string;
    width?: number;
    height?: number;
    loop?: boolean;
    autoplay?: boolean;
    Count?: string;
  };
}

export interface IChallengeResponse {
  challenge: IChallengeItem[];
  parameters: {
    HolderName?: string;
    ExpirationYear?: string;
    ExpirationMonth?: string;
    PaymentInstrumentToken?: string;
    CardBrand?: string;
    CardLast4Digits?: string;
    CardType?: string;
    OpenBankingField?: string;
    CureType?: string;
    TokenField?: string;
    FirstNameField?: string;
    LastNameField?: string;
    AcsURL?: string;
    EncodedCReq?: string;
    ThreeDSSessionData?: string;
    Redirect?: 'Approve' | 'Decline';
    Token?: string;
    LastName?: string;
    FirstName?: string;
    PlaidLinkedToken?: string;
  };
  result: string | null;
}

export function getChallenge(dispatch: React.Dispatch<IAction>, requestUrl?: string): Promise<IChallengeResponse> {
  const orderSessionKey = getSearchParameter('orderSessionKey');
  const mid = getSearchParameter('mid');
  const senseKey = getSearchParameter('senseKey');

  // eslint-disable-next-line no-restricted-globals
  parent.postMessage({ message: 'demoMessage', value: 'FlexFactor: Challenge required.' }, '*');

  const url = requestUrl || `${getCoreAPIUrl('eligibility')}/next`;
  // FIXME language
  const body = {
    orderSessionKey,
    senseKey,
    mid,
    language: 'string',
  };
  const options = {
    method: 'POST',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  };

  const transaction = Sentry.startTransaction({ name: 'GET_challenge' });
  const span = transaction.startChild({
    op: 'http',
    description: `POST ${url}`,
  });

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return fetch(url, options)
    .then(response => {
      if (response && response.status >= 200 && response.status < 300) {
        return response.json();
      } else {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        logger({ name: `Get challenge error (POST)`, message: { error: response?.message || response.status } });

        redirectToDecline();
      }
    })
    .then(response => {
      span.setData('GET challenge response', JSON.stringify(response));
      span.finish();
      transaction.finish();

      if (!response) {
        logger({ name: 'GET challenge response is empty', message: { error: 'response is empty' } });
        return redirectToDecline();
      }

      if (response?.parameters?.AutoRedirectUrl) {
        return redirect(response?.parameters?.AutoRedirectUrl, response?.parameters?.Redirect || '_self');
      }

      if (response?.result === 'NOT_ELIGIBLE' || !response?.challenge) {
        return redirectToDecline();
      }

      if (response?.parameters?.Redirect === 'Approve') {
        // eslint-disable-next-line no-restricted-globals
        parent.postMessage(
          { message: 'demoMessage', value: '<span class="green">FlexFactor: transaction approved.</span>' },
          '*',
        );
      }
      if (response?.parameters?.Redirect === 'Decline') {
        // eslint-disable-next-line no-restricted-globals
        parent.postMessage(
          { message: 'demoMessage', value: '<span class="green">FlexFactor: transaction declined.</span>' },
          '*',
        );
      }

      if (response?.parameters?.C_ID) {
        logger({ name: 'C_ID', message: response?.parameters?.C_ID });
      }

      dispatch({ type: commonReducerActions.SET_CHALLENGE_DATA, payload: response as IChallengeResponse });
    })
    .catch(error => {
      Sentry.captureMessage('WidgetShell: GET challenge response error', error.message);
      logger({ name: 'WidgetShell: GET challenge response error', message: error.message });

      span.setData('GET challenge response error', error.message);
      span.finish();
      transaction.finish();

      loggerError(error);
      redirectToDecline();
    });
}

export interface IChallengeAnswers {
  [key: string]: string | number | boolean | null;
}

// export function sendAnswers({ answers }: { answers: IChallengeAnswers }): Promise<IEligibilityApproveResponse> {
//   const orderSessionKey = getSearchParameter('orderSessionKey');
//   const mid = getSearchParameter('mid');
//   const senseKey = getSearchParameter('senseKey');
//
//   const url = `${getCoreAPIUrl('eligibility)}/next`;
//   // FIXME language
//   const body = {
//     orderSessionKey,
//     senseKey,
//     mid,
//     language: 'string',
//     answers,
//   };
//
//   const options = {
//     method: 'PUT',
//     mode: 'cors',
//     headers: {
//       'Content-Type': 'application/json',
//     },
//     body: JSON.stringify(body),
//   };
//
//   const transaction = Sentry.startTransaction({ name: 'PUT_challenge' });
//   const span = transaction.startChild({
//     op: 'http',
//     description: `PUT ${url}`,
//   });
//
//   // eslint-disable-next-line @typescript-eslint/ban-ts-comment
//   // @ts-ignore
//   return fetch(url, options)
//     .then(response => {
//       span.setData('PUT challenge response', JSON.stringify(response));
//       span.finish();
//       transaction.finish();
//       return response.json();
//     })
//     .catch(error => {
//       span.setData('PUT challenge response error', error.message);
//       span.finish();
//       transaction.finish();
//
//       loggerError(error);
//       redirectToDecline();
//     });
// }

export interface IRecacheCvv {
  cvv: string;
  mid: string | undefined;
  PaymentInstrumentToken: string | null | undefined;
  environment: string | number | string[] | null | boolean;
  requestUrl?: string;
}

export interface IRecacheCvvResponse {
  errors: any | null;
  result: string | null;
  transaction: {
    payment_method: {
      token: string;
    };
  };
  message: {
    id: string;
  };
}

export function recacheCvv(props: IRecacheCvv): Promise<IRecacheCvvResponse> {
  const { cvv, mid, PaymentInstrumentToken, environment } = props;
  const url = `${getCoreAPIUrl(
    'tokenize',
  )}/recache-cvv?mid=${mid}&environment=${environment}&paymentInstrumentToken=${PaymentInstrumentToken}`;

  const body = {
    payment_method: {
      credit_card: {
        verification_value: cvv,
      },
    },
  };
  const options = {
    method: 'POST',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  };

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return fetch(url, options).then(response => {
    if (response && response.status >= 200 && response.status < 300) {
      return response.json();
    } else {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      logger({ name: `POST recache cvv`, message: { error: response?.message || response.status } });

      redirectToDecline();
    }
  });
}

export interface ITokenizeCard {
  mid: string | undefined;
  environment: string | number | string[] | null | boolean;
  creditCard: {
    number: string;
    month: string;
    year: string;
    firstName: string;
    lastName: string;
    verificationValue: string;
  };
}

export interface ITokenizeCardResponse {
  errors: any | null;
  result: string | null;
  transaction: {
    payment_method: {
      token: string;
    };
  };
}

export async function tokenizeCard(props: ITokenizeCard): Promise<ITokenizeCardResponse> {
  const { mid, environment, creditCard } = props;
  const url = `${getCoreAPIUrl('tokenize')}/tokenize?mid=${mid}&environment=${environment}`;
  // const url = `https://api.flex-charge.com/tokenize?mid=${mid}&environment=${environment}`;

  const body = {
    payment_method: {
      credit_card: {
        number: creditCard.number,
        month: creditCard.month,
        year: creditCard.year,
        first_name: creditCard.firstName,
        last_name: creditCard.lastName,
        verification_value: creditCard.verificationValue,
      },
    },
  };
  const options = {
    method: 'POST',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  };

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return await fetch(url, options).then(response => {
    if (response && response.status >= 200 && response.status < 300) {
      return response.json();
    } else {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      logger({ name: `POST tokenize cardt error`, message: { error: response?.message || response.status } });

      redirectToDecline();
    }
  });
}

export function rejectOrder() {
  try {
    const orderSessionKey = getSearchParameter('orderSessionKey');
    const mid = getSearchParameter('mid');
    const senseKey = getSearchParameter('senseKey');

    const url = `${getCoreAPIUrl('eligibility')}/reject`;
    const body = {
      orderSessionKey,
      senseKey,
      mid,
    };

    const options = {
      method: 'POST',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    };

    const transaction = Sentry.startTransaction({ name: 'POST_reject_order' });
    const span = transaction.startChild({
      op: 'http',
      description: `POST ${url}`,
    });

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return fetch(url, options)
      .then(() => {
        span.setData('POST reject order response', 'OK');
        span.finish();
        transaction.finish();

        redirectToDecline();
      })
      .catch(error => {
        Sentry.captureMessage('WidgetShell: POST reject order error', error.message);
        logger({ name: 'WidgetShell:  POST reject order error', message: error.message });

        span.setData('POST reject response error', error.message);
        span.finish();
        transaction.finish();

        loggerError(error);
        redirectToDecline();
      });
  } catch (error) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    loggerError(error);
  }
}
